import React, { useState, useCallback } from "react";
import { observer } from "mobx-react";
import {
  Container,
  MasterContainer,
  LoaderWrapper,
  ErrorWrapper,
  EmptyWrapper,
} from "./Notes.styled";
import { Filters, Items, Paginator } from "./components";
import {
  CircularProgress,
  WidgetEmptyErrorPlaceholder,
} from "@ann/web-components";
import NotesSlideOut from "../../components/PreviewSlideOut/NotesSlideOut";
import { useActivityPage } from "../../../../hooks/useActivityPage";

const Notes = observer(
  ({
    items = [],
    meta,
    loading,
    error,
    contactId,
    onPageChange,
    onItemsPerPageChange,
    onRefresh,
  }) => {
    const [showCreateNoteSlideOut, setShowCreateNoteSlideOut] = useState(false);
    const [showUpdateNoteSlideOut, setShowUpdateNoteSlideOut] = useState(false);
    const [selectedNoteData, setSelectedNoteData] = useState(null);
    const hasItems = items.length > 0;

    const {
      handleCreateNote,
      handleGetNoteDetail,
      handleUpdateNote,
      handleDeleteNote,
    } = useActivityPage();

    const handleOpenCreate = useCallback(() => {
      setShowCreateNoteSlideOut(true);
    }, []);

    const handleCloseCreate = useCallback(() => {
      setShowCreateNoteSlideOut(false);
    }, []);

    const handleOpenUpdate = useCallback(
      async (note) => {
        try {
          const noteUuid =
            note.note_uuid ||
            note.uuid ||
            note.raw?.note_uuid ||
            note.raw?.uuid ||
            note.raw?.id ||
            note.raw?.noteId ||
            note.raw?.activity_uuid ||
            null;

          const baseNoteData = {
            note_uuid: noteUuid,
            note_title: note.title || "",
            note_content: note.description || "",
            category: note.category || "",
            last_update: note.lastUpdated || null,
            created_at: note.creationDate || null,
            raw: note.raw,
          };

          setSelectedNoteData(baseNoteData);
          setShowUpdateNoteSlideOut(true);

          if (noteUuid) {
            try {
              const fullNoteData = await handleGetNoteDetail(noteUuid);
              setSelectedNoteData((prev) => ({
                ...prev,
                ...fullNoteData,
                note_uuid: fullNoteData.note_uuid || noteUuid,
                raw: fullNoteData.raw || prev.raw,
              }));
            } catch (error) {
              console.error(
                "[Notes tab] Error loading note detail, using base data:",
                error
              );
            }
          }
        } catch (error) {
          console.error("[Notes tab] Error opening note for update:", error);
        }
      },
      [handleGetNoteDetail]
    );

    const handleCloseUpdate = useCallback(() => {
      setShowUpdateNoteSlideOut(false);
      setSelectedNoteData(null);
    }, []);

    const handleUpdateNoteSubmit = useCallback(
      async (noteData) => {
        try {
          await handleUpdateNote(noteData);
          setShowUpdateNoteSlideOut(false);
          setSelectedNoteData(null);
        } catch (error) {
          throw error;
        }
      },
      [handleUpdateNote]
    );

    const handleDeleteNoteClick = useCallback(
      async (note) => {
        try {
          const noteUuid =
            note.note_uuid ||
            note.uuid ||
            note.raw?.note_uuid ||
            note.raw?.uuid ||
            note.raw?.id ||
            note.raw?.noteId ||
            note.raw?.activity_uuid ||
            null;

          if (!noteUuid) {
            console.error("[Notes tab] Cannot delete note: note_uuid not found", note);
            return;
          }

          await handleDeleteNote(noteUuid);
        } catch (error) {
          console.error("[Notes tab] Error deleting note:", error);
        }
      },
      [handleDeleteNote]
    );

    return (
      <MasterContainer>
        <Container>
          <Filters />
          {loading ? (
            <LoaderWrapper>
              <CircularProgress size="medium" />
            </LoaderWrapper>
          ) : error ? (
            <ErrorWrapper>
              <WidgetEmptyErrorPlaceholder
                icon="error"
                title="Something went wrong"
                message="We couldn't load notes. Please try again."
                buttonText="Retry"
                showButton={true}
                onClick={onRefresh}
              />
            </ErrorWrapper>
          ) : hasItems ? (
            <Items items={items} onOpenNote={handleOpenUpdate} onDeleteNote={handleDeleteNoteClick} />
          ) : (
            <EmptyWrapper>
              <WidgetEmptyErrorPlaceholder
                icon="searchOff"
                title="No notes yet"
                message="Add notes to track preferences or key info"
                buttonText="Create note"
                showButton={true}
                onClick={handleOpenCreate}
              />
            </EmptyWrapper>
          )}
        </Container>

        {hasItems && (
          <Paginator
            meta={meta}
            loading={loading}
            onPageChange={onPageChange}
            onItemsPerPageChange={onItemsPerPageChange}
          />
        )}

        {/* Slideout de creación */}
        <NotesSlideOut
          open={showCreateNoteSlideOut}
          onClose={handleCloseCreate}
          mode="create"
          onCreateNote={handleCreateNote}
        />
        <NotesSlideOut
          open={showUpdateNoteSlideOut}
          onClose={handleCloseUpdate}
          mode="update"
          noteData={selectedNoteData}
          onUpdateNote={handleUpdateNoteSubmit}
        />
      </MasterContainer>
    );
  }
);

export default Notes;
